import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import {
    useBioscopeImageController_GetDaysWithImages,
    useBioscopeImageController_GetImageList,
} from '@/services/hooks';
import { BioscopeImageController_Image, template } from '@/services/services';

import { asArray, toBioscopeImageListQuery } from './mappers';
import type { BioscopeImageDay, BioscopeImageMeta, UseBioscopeImagesResult } from './types';
import { useNeighbourDayPrefetch } from './useNeighbourDayPrefetch';
import { useScopeImagesToEntity, useSelectedDayForEntity } from './useScopedBioscopeImages';

export function useGetBioscopeImages(
    bioscopeId: number,
    permalinkImageId?: string
): UseBioscopeImagesResult {
    const queryClient = useQueryClient();
    const { selectedDay, onDayChange } = useSelectedDayForEntity(bioscopeId);
    const queryParams = toBioscopeImageListQuery(selectedDay, permalinkImageId);
    const enabled = bioscopeId > 0;

    const daysQuery = useBioscopeImageController_GetDaysWithImages(bioscopeId, {
        enabled,
    });
    const imagesQuery = useBioscopeImageController_GetImageList(bioscopeId, queryParams, {
        enabled,
        placeholderData: keepPreviousData,
    });

    const days = useMemo(
        () => asArray<BioscopeImageDay>(daysQuery.data?.data),
        [daysQuery.data?.data]
    );
    const rawImages = useMemo(
        () => asArray<BioscopeImageMeta>(imagesQuery.data?.data),
        [imagesQuery.data?.data]
    );
    const { images, isLoading, isPreviousData, isError } = useScopeImagesToEntity(
        bioscopeId,
        imagesQuery.isPlaceholderData,
        imagesQuery.isPending,
        Boolean(imagesQuery.isError),
        rawImages
    );

    const prefetch = useCallback(
        (day: string) =>
            useBioscopeImageController_GetImageList.prefetch(queryClient, bioscopeId, { day }),
        [queryClient, bioscopeId]
    );
    const readFirstImage = useCallback(
        (day: string): BioscopeImageMeta | undefined => {
            const { key } = useBioscopeImageController_GetImageList.info(bioscopeId, { day });
            const cached = queryClient.getQueryData(key) as { data?: unknown } | undefined;
            return asArray<BioscopeImageMeta>(cached?.data)[0];
        },
        [queryClient, bioscopeId]
    );
    const jpegSrc = useCallback(
        (imageId: string): string =>
            template(BioscopeImageController_Image.key, { bioscopeId, id: imageId }),
        [bioscopeId]
    );

    useNeighbourDayPrefetch({
        days,
        selectedDay,
        enabled,
        prefetch,
        readFirstImage,
        jpegSrc,
    });

    const { refetch: refetchImages } = imagesQuery;
    const retryImages = useCallback(() => {
        void refetchImages();
    }, [refetchImages]);

    return {
        images,
        days,
        isLoading,
        isPreviousData,
        isError,
        isFetching: imagesQuery.isFetching,
        refetch: retryImages,
        onDayChange,
    };
}
